import { supabase } from "@/integrations/supabase/client";

export const REFERRAL_KEY = "td_ref"; // stores a username slug

const USERNAME_RE = /^[a-z0-9][a-z0-9_-]{0,40}$/i;

/** Capture ?ref=<uuid> (legacy) or a `/{username}/invite` path segment. */
export function captureRefFromUrl(): void {
  if (typeof window === "undefined") return;
  try {
    const url = new URL(window.location.href);
    // Legacy: ?ref=<uuid> — kept for backwards-compatibility.
    const legacy = url.searchParams.get("ref");
    if (legacy) {
      window.localStorage.setItem(REFERRAL_KEY, legacy);
      return;
    }
    // New: /{username}/invite
    const m = url.pathname.match(/^\/([^/]+)\/invite\/?$/i);
    if (m && USERNAME_RE.test(m[1])) {
      window.localStorage.setItem(REFERRAL_KEY, m[1]);
    }
  } catch {
    /* ignore */
  }
}

/** Explicitly store a username referrer (used by the invite landing page). */
export function storeUsernameRef(username: string): void {
  if (typeof window === "undefined") return;
  if (!USERNAME_RE.test(username)) return;
  try {
    window.localStorage.setItem(REFERRAL_KEY, username);
  } catch {
    /* ignore */
  }
}

export function getStoredRef(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(REFERRAL_KEY);
  } catch {
    return null;
  }
}

export function clearStoredRef(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(REFERRAL_KEY);
  } catch {
    /* ignore */
  }
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Resolve stored ref (username or legacy uuid) → referrer user_id. */
async function resolveRefToUserId(ref: string): Promise<string | null> {
  if (UUID_RE.test(ref)) return ref;
  if (!USERNAME_RE.test(ref)) return null;
  const { data } = await supabase.rpc("get_user_id_by_username", { _username: ref });
  return (data as string | null) ?? null;
}

/**
 * If the current user has a stored referral and no referred_by yet,
 * write it once. Ignores self-referrals silently.
 */
export async function applyStoredReferral(userId: string): Promise<void> {
  const ref = getStoredRef();
  if (!ref) return;
  const referrerId = await resolveRefToUserId(ref);
  if (!referrerId || referrerId === userId) {
    clearStoredRef();
    return;
  }
  const { data, error } = await supabase
    .from("profiles")
    .select("referred_by")
    .eq("user_id", userId)
    .maybeSingle();
  if (error || !data) return;
  if (data.referred_by) {
    clearStoredRef();
    return;
  }
  const { error: updErr } = await supabase
    .from("profiles")
    .update({ referred_by: referrerId })
    .eq("user_id", userId)
    .is("referred_by", null);
  if (!updErr) clearStoredRef();
}

/** Каноничный домен сообщества — им делимся в мессенджерах. */
export const PUBLIC_ORIGIN = "https://turkey-drive.com";

/** Build the shareable invite link for a user, given their username. */
export function buildInviteLink(username: string): string {
  return `${PUBLIC_ORIGIN}/${username}/invite`;
}

/** Сопроводительный текст для шеринга приглашения. */
export function inviteShareText(link: string): string {
  return `Присоединяйся к TurkeyDrive — сообществу русскоязычных в Турции: люди, встречи, помощь 🐾 ${link}`;
}

/** «1 человек», «4 человека», «7 человек». */
export function pluralPeople(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return "человек";
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return "человека";
  return "человек";
}
